import {Badge, createStyles, Stack} from '@mantine/core';
import React, {useCallback, useState} from 'react';

import {shouldSuppressNotification} from 'BenchlingUtil';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import ChatSettingsListItem from 'llm/chat-library/shared/list-detail/ChatSettingsListItem';
import ChatSettingsPane from 'llm/chat-library/shared/list-detail/ChatSettingsPane';
import {getChatUseCaseCategories, ChatUseCase} from 'llm/chat-library/use-cases/chatUseCaseCatalog';
import {
  isChatUseCaseNew,
  sortNewFirst,
  useUsedChatUseCaseIds,
} from 'llm/chat-library/use-cases/chatUseCaseUsage';
import runChatUseCase from 'llm/chat-library/use-cases/runChatUseCase';
import notifications from 'notifications';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {warn} from 'util/Logging';

const TEST_COMPONENT = 'ChatSettingsUseCasesContent';
const ALL_CATEGORIES = 'ALL';
/** Fits the longest category label ("Data visualization") without stretching across the pane. */
const CATEGORY_FILTER_WIDTH_PX = 220;

const useStyles = createStyles((theme) => ({
  categoryFilter: {
    width: CATEGORY_FILTER_WIDTH_PX,
  },
  statusBadge: {
    // These read as status, not as shouted labels, so drop the badge default of upper case.
    textTransform: 'none',
    // The title truncates before the badge does, so a long title never squeezes it out.
    flexShrink: 0,
    // An xs badge keeps the title's font size but shrinks its line box around it, which reads as
    // oversized next to the title. Step the text down and give it room to sit on its own line.
    fontSize: theme.fontSizes.xs,
    lineHeight: theme.lineHeight,
  },
}));

interface ChatUseCaseRow extends ChatUseCase {
  categoryId: string;
}

/**
 * The row's standing for this user: recently added and still unrun, or already run. A use case can
 * never be both, since running one stops it reading as new.
 */
function renderStatusBadge(
  row: ChatUseCaseRow,
  usedChatUseCaseIds: ReadonlySet<string>,
  className: string
): JSX.Element | undefined {
  if (isChatUseCaseNew(row, usedChatUseCaseIds)) {
    return (
      <Badge color="blue" size="xs" variant="light" radius="sm" className={className}>
        New
      </Badge>
    );
  }
  if (usedChatUseCaseIds.has(row.id)) {
    return (
      <Badge color="gray" size="xs" variant="light" radius="sm" className={className}>
        Already run
      </Badge>
    );
  }
  return undefined;
}

/**
 * The Use cases tab: every use case in the catalog as a list row, filtered by a single-select
 * category dropdown.
 *
 * Only recently added use cases are lifted to the top. Every other row keeps catalog order,
 * including ones the user has already run: those carry a badge that says so, and moving a row the
 * user just used would pull it out from under them.
 *
 * The selected category is local state rather than URL or modal state: it is a transient view
 * filter with no deep-link requirement, unlike the Skills and Agents group filters.
 */
export default function ChatSettingsUseCasesContent(): JSX.Element {
  const {classes} = useStyles();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(ALL_CATEGORIES);
  // Running a use case creates a chat, so block every Run while one is in flight rather than only
  // the row that was clicked; a second click would otherwise create a second chat.
  const [isRunning, setIsRunning] = useState(false);
  const usedChatUseCaseIds = useUsedChatUseCaseIds();

  // Read here rather than in the catalog: LaunchDarkly reaches redux and analytics, and analytics
  // imports the catalog, so reading it there would close an import cycle.
  const {llmEnableManageRoutineTool} = useRolloutFlags();
  const categories = getChatUseCaseCategories({
    isManageRoutineToolEnabled: llmEnableManageRoutineTool === true,
  });
  const categoryOptions = [
    {value: ALL_CATEGORIES, label: 'All categories'},
    ...categories.map((category) => ({value: category.id, label: category.label})),
  ];
  const selectedCategoryLabel =
    categoryOptions.find((option) => option.value === selectedCategoryId)?.label ?? 'All categories';

  const rows: Array<ChatUseCaseRow> = sortNewFirst(
    categories
      .filter((category) => selectedCategoryId === ALL_CATEGORIES || category.id === selectedCategoryId)
      .flatMap((category) =>
        category.chatUseCases.map((chatUseCase) => ({...chatUseCase, categoryId: category.id}))
      ),
    usedChatUseCaseIds
  );

  const handleRun = useCallback(
    (row: ChatUseCaseRow) => {
      if (isRunning) {
        return;
      }
      setIsRunning(true);
      runChatUseCase(row, {categoryId: row.categoryId})
        .catch((e: unknown) => {
          // Chat creation already surfaces insufficient credits with its own persistent warning and
          // marks the error. A second, generic "try again" would both duplicate it and misadvise: a
          // retry cannot succeed until the tenant has credits.
          if (shouldSuppressNotification(e as {__suppress_notification?: boolean})) {
            return;
          }
          warn('[llm] Failed to run use case', {extra: {chatUseCaseId: row.id, error: e}});
          notifications.error('Could not start a chat for this use case. Try again.');
        })
        .finally(() => {
          setIsRunning(false);
        });
    },
    [isRunning]
  );

  return (
    <ChatSettingsPane header={{title: 'Use cases'}}>
      <Stack spacing="md">
        {/* The app's standard select dropdown, sized to the control rather than the pane so it
            reads as a filter over the list below instead of a form field to fill in. */}
        <SelectDropdown
          ariaLabel="Filter use cases by category"
          displayName={selectedCategoryLabel}
          className={classes.categoryFilter}
          dataTestAttrs={{component: TEST_COMPONENT, element: 'categoryFilter'}}
          menu={
            <Menu value={selectedCategoryId} width={MenuWidth.MEDIUM}>
              {categoryOptions.map(({value, label}) => (
                <Option<string>
                  key={value}
                  value={value}
                  onSelect={(categoryId) => {
                    setSelectedCategoryId(categoryId ?? ALL_CATEGORIES);
                  }}
                >
                  {label}
                </Option>
              ))}
            </Menu>
          }
        />
        <Stack spacing="md">
          {rows.map((row) => (
            <ChatSettingsListItem
              key={row.id}
              item={{
                id: row.id,
                title: row.label,
                titleBadge: renderStatusBadge(row, usedChatUseCaseIds, classes.statusBadge),
                description: row.prompt,
                primaryAction: {
                  label: 'Run',
                  ariaLabel: `Run ${row.label}`,
                  disabled: isRunning,
                  onClick: () => {
                    handleRun(row);
                  },
                },
              }}
              selected={false}
            />
          ))}
        </Stack>
      </Stack>
    </ChatSettingsPane>
  );
}
